/* eslint-disable @reatom/unit-naming-rule */
import { Result } from '@blade/std-lib'
import { Network as NetworkModel } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomResource } from '@root/shared/lib'

import type { TickerRow } from '../../model/ticker-strip'
import type { Network } from '@root/entities/network'

/**
 * The tokens the strip runs along the top.
 *
 * It is deliberately not the live feed the board uses: that one keeps a
 * registry of pair models and a socket subscription per row, which is the
 * right cost for a screen made of those rows and an absurd one for a strip
 * that shows a ticker and a number.
 *
 * It is no longer the trending *read* either. That answered with a pool card
 * each — thirty-five fields, a sparkline, five windows of period stats and the
 * accounts a swap needs. Measured on production 2026-10-02: **73.5 KiB
 * compressed for thirty cards, against 3.6 KiB** from the route that returns
 * the five fields a strip can draw. Twenty times smaller and half the time.
 *
 * The rows arrive flat and are shaped here into what the reading expects, so
 * `tickerItems` and its tests do not have to know where they came from.
 */
export const reatomTickerFeed = ({ network }: { network: Network.Network }) =>
  reatomResource(
    async (ctx) => {
      const { tradeApiClient } = ctx.get(dependenciesAtom)

      const result = await ctx.schedule(() =>
        apiRequest(ctx, tradeApiClient.services.servicesPoolsTickerCreate, [
          { network: NetworkModel.toDto(network) },
        ]),
      )

      return Result.map(
        Result.mapError(result, ApiError.fallbackToUnknown),
        (rows): ReadonlyArray<TickerRow> =>
          rows.map((row) => ({
            poolAddress: row.poolAddress,
            quote: { symbol: row.symbol, logo: row.logo },
            marketCapUsd: row.marketCapUsd,
            periodStats: {
              '1D': { priceChangePercents: row.priceChangePercents },
            },
          })),
      )
    },
    { name: 'reatomTickerFeed', loadOnConnect: true },
  )
